import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Paper,
  TextField,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  LinearProgress,
  Alert,
  Divider,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormControl,
  FormLabel
} from '@mui/material';
import { debounce } from 'lodash';
import ReadOnlySimulator from './ReadOnlySimulator';
import {
  Quiz,
  QuizLink,
  QuizQuestion,
  upsertQuizResponse,
  submitQuiz,
  getQuizResponses
} from '../../services/quizService';

interface QuizTakerProps {
  quiz: Quiz;
  quizLink: QuizLink;
  questions: QuizQuestion[];
  onComplete: () => void;
}

const QuizTaker: React.FC<QuizTakerProps> = ({ quiz, quizLink, questions, onComplete }) => {
  const [answers, setAnswers] = useState<Record<string, string>>({});
  const [timeRemaining, setTimeRemaining] = useState<number>(0);
  const [showSubmitDialog, setShowSubmitDialog] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [startTime] = useState<number>(Date.now());

  // Load existing responses
  useEffect(() => {
    const loadResponses = async () => {
      try {
        const responses = await getQuizResponses(quizLink.id);
        const answersMap: Record<string, string> = {};
        responses.forEach(response => {
          if (response.response_text) {
            answersMap[response.question_id] = response.response_text;
          }
        });
        setAnswers(answersMap);
      } catch (err) {
        console.error('Error loading responses:', err);
      }
    };

    loadResponses();
  }, [quizLink.id]);

  // Timer countdown
  useEffect(() => {
    const calculateTimeRemaining = () => {
      const now = Date.now();
      const expiry = new Date(quizLink.expires_at).getTime();
      const remaining = Math.max(0, expiry - now);
      return remaining;
    };

    setTimeRemaining(calculateTimeRemaining());

    const interval = setInterval(() => {
      const remaining = calculateTimeRemaining();
      setTimeRemaining(remaining);

      if (remaining === 0) {
        handleAutoSubmit();
      }
    }, 1000);

    return () => clearInterval(interval);
  }, [quizLink.expires_at]);

  // Auto-save functionality with debounce
  const saveResponse = async (questionId: string, text: string) => {
    try {
      await upsertQuizResponse(quizLink.id, questionId, text);
    } catch (err) {
      console.error('Error saving response:', err);
      setError('Failed to save response. Please check your connection.');
    }
  };

  const debouncedSave = useCallback(
    debounce((questionId: string, text: string) => {
      saveResponse(questionId, text);
    }, 500),
    [quizLink.id]
  );

  const handleAnswerChange = (questionId: string, text: string) => {
    setAnswers(prev => ({ ...prev, [questionId]: text }));
    debouncedSave(questionId, text);
  };

  const handleAutoSubmit = async () => {
    const timeTaken = (Date.now() - startTime) / 60000; // Convert to minutes
    try {
      await submitQuiz(quizLink.id, timeTaken);
      onComplete();
    } catch (err) {
      console.error('Error auto-submitting quiz:', err);
      setError('Failed to submit quiz automatically.');
    }
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    setError(null);

    const timeTaken = (Date.now() - startTime) / 60000; // Convert to minutes

    try {
      await submitQuiz(quizLink.id, timeTaken);
      setShowSubmitDialog(false);
      onComplete();
    } catch (err) {
      console.error('Error submitting quiz:', err);
      setError('Failed to submit quiz. Please try again.');
      setSubmitting(false);
    }
  };

  // Format time remaining
  const formatTime = (ms: number): { text: string; isLow: boolean } => {
    const totalSeconds = Math.floor(ms / 1000);
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;

    const isLow = totalSeconds < 300; // Less than 5 minutes

    if (hours > 0) {
      return { text: `${hours}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`, isLow };
    }
    return { text: `${minutes}:${seconds.toString().padStart(2, '0')}`, isLow };
  };

  const timeDisplay = formatTime(timeRemaining);
  const progress = ((Date.now() - startTime) / (new Date(quizLink.expires_at).getTime() - startTime)) * 100;

  return (
    <Box>
      {/* Header with timer */}
      <Paper elevation={3} sx={{ p: 3, mb: 3, borderRadius: 2 }}>
        <Typography variant="h4" gutterBottom sx={{ fontWeight: 'bold' }}>
          {quiz.title}
        </Typography>
        {quiz.description && (
          <Typography variant="body1" color="text.secondary" paragraph>
            {quiz.description}
          </Typography>
        )}

        <Divider sx={{ my: 2 }} />

        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
          <Typography variant="body2" color="text.secondary">
            Participant: <strong>{quizLink.participant_name}</strong>
          </Typography>
          <Typography
            variant="h5"
            sx={{
              fontWeight: 'bold',
              color: timeDisplay.isLow ? 'error.main' : 'primary.main',
              transition: 'color 0.3s'
            }}
          >
            Time Remaining: {timeDisplay.text}
          </Typography>
        </Box>

        <LinearProgress
          variant="determinate"
          value={Math.min(progress, 100)}
          sx={{
            height: 8,
            borderRadius: 1,
            '& .MuiLinearProgress-bar': {
              backgroundColor: timeDisplay.isLow ? 'error.main' : 'primary.main'
            }
          }}
        />
      </Paper>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Questions */}
      {questions.map((question, index) => (
        <Paper key={question.id} elevation={2} sx={{ p: 3, mb: 3, borderRadius: 2 }}>
          <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold', mb: 2 }}>
            Question {index + 1}
          </Typography>

          <Typography variant="body1" paragraph sx={{ whiteSpace: 'pre-wrap' }}>
            {question.question_text}
          </Typography>

          {question.simulator_state && question.simulator_state.length > 0 && (
            <Box sx={{ my: 3 }}>
              <ReadOnlySimulator playerStates={question.simulator_state} />
            </Box>
          )}

          {question.question_type === 'free_text' ? (
            <TextField
              fullWidth
              multiline
              rows={4}
              label="Your Answer"
              value={answers[question.id] || ''}
              onChange={(e) => handleAnswerChange(question.id, e.target.value)}
              variant="outlined"
              sx={{ mt: 2 }}
              placeholder="Type your answer here..."
            />
          ) : (
            <FormControl component="fieldset" sx={{ mt: 2, width: '100%' }}>
              <FormLabel component="legend" sx={{ mb: 1 }}>Select your answer:</FormLabel>
              <RadioGroup
                value={answers[question.id] || ''}
                onChange={(e) => handleAnswerChange(question.id, e.target.value)}
              >
                {question.options?.map((option) => (
                  <FormControlLabel
                    key={option.id}
                    value={option.id}
                    control={<Radio />}
                    label={option.text}
                    sx={{ mb: 1 }}
                  />
                ))}
              </RadioGroup>
            </FormControl>
          )}
        </Paper>
      ))}

      {/* Submit Button */}
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4, mb: 4 }}>
        <Button
          variant="contained"
          size="large"
          onClick={() => setShowSubmitDialog(true)}
          sx={{ minWidth: 200 }}
        >
          Submit Quiz
        </Button>
      </Box>

      {/* Submit Confirmation Dialog */}
      <Dialog open={showSubmitDialog} onClose={() => !submitting && setShowSubmitDialog(false)}>
        <DialogTitle>Submit Quiz?</DialogTitle>
        <DialogContent>
          <Typography>
            This cannot be undone. Once you submit, all your answers are final.
          </Typography>
          <Typography sx={{ mt: 2, fontWeight: 'bold' }}>
            Are you sure you want to submit?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowSubmitDialog(false)} disabled={submitting}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} variant="contained" disabled={submitting}>
            {submitting ? 'Submitting...' : 'Yes, Submit'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default QuizTaker;
